import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import { toast } from "sonner";

const initial = { name: "", email: "", password: "", company: "", area: "", city: "", country: "" };

export default function DistributorRegister() {
  const { registerDistributor, user } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (user?.role === "distributor") nav("/distributors/portal", { replace: true });
  }, [user, nav]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await registerDistributor(form);
      toast.success(t("dist.welcomeToast"));
      nav("/distributors/portal", { replace: true });
    } catch (err) { toast.error(err.message || t("dist.regFailed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-screen bg-[#F7F5EE] text-[#0F1A12] overflow-hidden flex items-center justify-center p-6" data-testid="distributor-register">
      <img
        aria-hidden
        src="/hupla-emblem.svg"
        alt=""
        className="pointer-events-none select-none absolute -right-40 -bottom-40 w-[720px] max-w-none opacity-[0.06]"
      />
      <button
        type="button"
        onClick={() => nav("/")}
        aria-label={t("dist.closeAria") || "Close"}
        data-testid="dist-register-close"
        className="absolute top-5 right-5 z-10 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white shadow-sm hover:bg-black/5 text-black/70 border border-black/10 transition-colors"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-sm p-8 border border-black/5">
        <p className="text-[0.7rem] font-bold uppercase tracking-[0.36em] text-[#B02224] mb-3">{t("dist.portalOverline")}</p>
        <h1 className="font-editorial italic text-3xl mb-2">{t("dist.registerTitle")}</h1>
        <p className="text-sm text-black/60 mb-6">{t("dist.registerSub")}</p>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <input required minLength={2} placeholder={t("dist.phName")} value={form.name} onChange={update("name")} className="w-full rounded-lg border border-black/15 px-3 py-2.5 text-sm" data-testid="dist-reg-name" />
            <input required minLength={2} placeholder={t("dist.phCompany")} value={form.company} onChange={update("company")} className="w-full rounded-lg border border-black/15 px-3 py-2.5 text-sm" data-testid="dist-reg-company" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <input required type="email" placeholder={t("dist.phEmail")} value={form.email} onChange={update("email")} className="w-full rounded-lg border border-black/15 px-3 py-2.5 text-sm" data-testid="dist-reg-email" />
            <input required type="password" minLength={8} placeholder={t("dist.phPassword")} value={form.password} onChange={update("password")} className="w-full rounded-lg border border-black/15 px-3 py-2.5 text-sm" data-testid="dist-reg-password" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-black/50 pt-2">{t("dist.territoryLabel")}</p>
          <div className="grid md:grid-cols-3 gap-3">
            <input required placeholder={t("dist.phArea")} value={form.area} onChange={update("area")} className="w-full rounded-lg border border-black/15 px-3 py-2.5 text-sm" data-testid="dist-reg-area" />
            <input required placeholder={t("dist.phCity")} value={form.city} onChange={update("city")} className="w-full rounded-lg border border-black/15 px-3 py-2.5 text-sm" data-testid="dist-reg-city" />
            <input required placeholder={t("dist.phCountry")} value={form.country} onChange={update("country")} className="w-full rounded-lg border border-black/15 px-3 py-2.5 text-sm" data-testid="dist-reg-country" />
          </div>
          <button type="submit" disabled={busy} className="w-full mt-3 rounded-full bg-[#0F1A12] text-white py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="dist-reg-submit">
            {busy ? t("dist.creating") : t("dist.createBtn")}
          </button>
        </form>
        <p className="mt-6 text-xs text-black/60">
          {t("dist.alreadyRegistered")} <Link to="/distributors/login" className="underline hover:text-black" data-testid="dist-reg-to-login">{t("dist.signInLink")}</Link>
        </p>
      </div>
    </div>
  );
}
